import { ArrowClockwise, BookOpen, Check, Copy, FilePdf, Globe, ImageSquare, LockKey, Minus, Plus, Trash, VideoCamera, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { deleteLocalViewFile, saveLocalViewFile } from '../lib/localContentStore';
import { allowsVideo, mediaAccept, mediaHint, mediaLabel, resolveMediaPlayback, supportsMedia as kindSupportsMedia } from '../lib/mediaSupport';
import type { PlacedObject } from '../types';

const colors = ['#f3f1e8', '#c9c5ba', '#53634e', '#b26748', '#33474a', '#272b2a'];

interface InspectorPanelProps {
  object: PlacedObject | null;
  wallCount: number;
  roomHeight: number;
  onUpdate: (patch: Partial<PlacedObject>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

function meters(value: number) {
  return `${Math.round(value * 1000).toLocaleString('ko-KR')} mm`;
}

export function InspectorPanel({ object, wallCount, roomHeight, onUpdate, onDelete, onDuplicate }: InspectorPanelProps) {
  const [websiteDraft, setWebsiteDraft] = useState('');

  useEffect(() => {
    setWebsiteDraft(object?.viewContent?.type === 'website' ? object.viewContent.url ?? '' : '');
  }, [object?.id, object?.viewContent]);

  if (!object) {
    return (
      <aside className="inspector-panel inspector-empty" aria-label="속성 패널">
        <div className="empty-orbit" aria-hidden="true"><span /></div>
        <h2>오브젝트를 선택하세요</h2>
        <p>배치한 물체를 누르면 위치, 회전, 색상과 미디어를 조절할 수 있습니다.</p>
      </aside>
    );
  }
  const supportsMedia = kindSupportsMedia(object.kind);
  const playback = resolveMediaPlayback(object.kind, object.mediaName, object.mediaUrl);
  const label = mediaLabel(object.kind);

  const updatePosition = (index: 0 | 2, value: string) => {
    const next = [...object.position] as [number, number, number];
    next[index] = Number(value) || 0;
    onUpdate({ position: next });
  };

  const handleMedia = (file: File | undefined) => {
    if (!file) return;
    if (object.mediaUrl?.startsWith('blob:')) URL.revokeObjectURL(object.mediaUrl);
    onUpdate({ mediaUrl: URL.createObjectURL(file), mediaName: file.name });
  };

  const clearViewContent = async () => {
    if (object.viewContent?.storage === 'local' && object.viewContent.localKey) {
      await deleteLocalViewFile(object.viewContent.localKey).catch(() => undefined);
    }
    onUpdate({ viewContent: undefined, viewEnabled: false });
    setWebsiteDraft('');
  };

  const handleViewFile = async (type: 'pdf' | 'image', file: File | undefined) => {
    if (!file) return;
    const previousKey = object.viewContent?.storage === 'local' ? object.viewContent.localKey : undefined;
    const localKey = await saveLocalViewFile(file);
    if (previousKey) await deleteLocalViewFile(previousKey).catch(() => undefined);
    onUpdate({
      viewEnabled: true,
      viewContent: { type, name: file.name, storage: 'local', localKey },
    });
  };

  const saveWebsite = async () => {
    const raw = websiteDraft.trim();
    if (!raw) return;
    const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    let hostname = '';
    try {
      hostname = new URL(url).hostname;
    } catch {
      return;
    }
    if (object.viewContent?.storage === 'local' && object.viewContent.localKey) {
      await deleteLocalViewFile(object.viewContent.localKey).catch(() => undefined);
    }
    onUpdate({
      viewEnabled: true,
      viewContent: { type: 'website', name: hostname, storage: 'local', url },
    });
    setWebsiteDraft(url);
  };

  const setRotation = (value: number) => {
    const normalized = ((Math.round(value) % 360) + 360) % 360;
    onUpdate({ rotation: normalized });
  };

  const supportsWallMount = object.kind === 'tv' || object.kind === 'scroll-banner';
  const mountMode = object.mountMode ?? (object.kind === 'scroll-banner' ? 'wall' : 'floor');
  const isWallMounted = Boolean(object.wallMount && mountMode === 'wall');

  return (
    <aside className="inspector-panel" aria-label={`${object.name} 속성`}>
      <div className="inspector-heading">
        <div>
          <span className="selection-label">선택됨</span>
          <h2>{object.name}</h2>
        </div>
        <div className="inline-actions">
          <button className="icon-button" type="button" onClick={onDuplicate} aria-label="복제"><Copy size={17} /></button>
          <button className="icon-button danger" type="button" onClick={onDelete} aria-label="삭제"><Trash size={17} /></button>
        </div>
      </div>

      <section className="property-section">
        <div className="property-title"><h3>실측 크기</h3><span className="locked"><LockKey size={13} /> 고정</span></div>
        <div className="dimension-grid">
          <div><span>너비</span><strong>{meters(object.dimensions.width)}</strong></div>
          <div><span>높이</span><strong>{meters(object.dimensions.height)}</strong></div>
          <div><span>깊이</span><strong>{meters(object.dimensions.depth)}</strong></div>
        </div>
        <p className="property-help">실제 제품 치수와 동일하여 크기를 변경할 수 없습니다.</p>
      </section>

      <section className="property-section">
        <h3>{isWallMounted ? '벽면 위치' : '바닥 위치'}</h3>
        {!isWallMounted ? (
          <div className="position-fields">
            <label><span>X</span><input type="number" step="0.05" value={object.position[0]} onChange={(event) => updatePosition(0, event.target.value)} /><em>m</em></label>
            <label><span>Z</span><input type="number" step="0.05" value={object.position[2]} onChange={(event) => updatePosition(2, event.target.value)} /><em>m</em></label>
          </div>
        ) : object.wallMount && (
          <div className="wall-mount-fields">
            <label>
              <span>벽</span>
              <select
                value={object.wallMount.segmentIndex}
                onChange={(event) => onUpdate({ wallMount: { ...object.wallMount!, segmentIndex: Number(event.target.value) } })}
              >
                {Array.from({ length: wallCount }, (_, index) => (
                  <option key={index} value={index}>벽 {index + 1}</option>
                ))}
              </select>
            </label>
            <label><span>좌우</span><input type="number" step="0.1" value={object.wallMount.offset} onChange={(event) => onUpdate({ wallMount: { ...object.wallMount!, offset: Number(event.target.value) || 0 } })} /><em>m</em></label>
            <label><span>높이</span><input type="number" step="0.05" min={0.2} max={roomHeight - 0.1} value={object.wallMount.height} onChange={(event) => onUpdate({ wallMount: { ...object.wallMount!, height: Number(event.target.value) || 0.2 } })} /><em>m</em></label>
          </div>
        )}
      </section>

      {supportsWallMount && (
        <section className="property-section">
          <div className="property-title"><h3>벽걸이</h3><span className="recommended">전시 부스</span></div>
          <div className="cover-options" role="radiogroup" aria-label="벽걸이 모드">
            {([
              ['floor', '바닥/스탠드'],
              ['wall', '벽면 부착'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={mountMode === value}
                className={mountMode === value ? 'active' : ''}
                onClick={() => {
                  if (value === 'wall') {
                    const segmentIndex = object.wallMount?.segmentIndex ?? 0;
                    const height = object.kind === 'scroll-banner'
                      ? object.dimensions.height / 2 + 0.2
                      : object.dimensions.height / 2;
                    onUpdate({
                      mountMode: 'wall',
                      wallMount: object.wallMount ?? { segmentIndex, offset: 0, height },
                    });
                  } else {
                    onUpdate({ mountMode: 'floor', wallMount: undefined });
                  }
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="property-help">벽면 부착 시 벽을 따라 좌우로 드래그해 위치를 조절할 수 있습니다.</p>
        </section>
      )}

      {!isWallMounted && (
      <section className="property-section">
        <div className="property-title"><h3>회전</h3><ArrowClockwise size={15} /></div>
        <div className="degree-control">
          <button type="button" onClick={() => setRotation(object.rotation - 1)} aria-label="1도 반시계 회전"><Minus size={15} /></button>
          <label><span className="sr-only">회전 각도</span><input type="number" min="0" max="359" step="1" value={object.rotation} onChange={(event) => setRotation(Number(event.target.value))} /><em>°</em></label>
          <button type="button" onClick={() => setRotation(object.rotation + 1)} aria-label="1도 시계 회전"><Plus size={15} /></button>
        </div>
        <div className="rotation-options" role="radiogroup" aria-label="회전 각도">
          {([0, 90, 180, 270] as const).map((angle) => (
            <button key={angle} role="radio" aria-checked={object.rotation === angle} className={object.rotation === angle ? 'active' : ''} type="button" onClick={() => setRotation(angle)}>{angle}°</button>
          ))}
        </div>
        <p className="property-help">버튼으로 1°씩 조절하거나 기준 각도를 빠르게 선택하세요.</p>
      </section>
      )}

      {object.kind === 'table' && (
        <section className="property-section">
          <div className="property-title"><h3>테이블 덮개</h3><span className="recommended">현장 연출</span></div>
          <div className="cover-options" role="radiogroup" aria-label="테이블 덮개">
            {([
              ['none', '없음'],
              ['white', '흰색 천'],
              ['black', '검은색 천'],
            ] as const).map(([value, label]) => (
              <button key={value} role="radio" aria-checked={(object.cover ?? 'none') === value} className={(object.cover ?? 'none') === value ? 'active' : ''} type="button" onClick={() => onUpdate({ cover: value })}>
                <span className={`cover-swatch cover-${value}`} />{label}
              </button>
            ))}
          </div>
          <p className="property-help">상판과 네 면을 바닥 가까이까지 덮는 전시용 천입니다.</p>
        </section>
      )}

      <section className="property-section">
        <div className="property-title"><h3>색상</h3><span className="recommended">기본값 추천</span></div>
        <div className="color-options">
          {colors.map((color) => (
            <button
              key={color}
              className={object.color === color ? 'active' : ''}
              style={{ '--swatch': color } as React.CSSProperties}
              type="button"
              onClick={() => onUpdate({ color })}
              aria-label={`색상 ${color}`}
            />
          ))}
        </div>
      </section>

      {supportsMedia && (
        <section className="property-section media-section">
          <h3>{label}</h3>
          {object.mediaUrl && playback === 'video' && (
            <div className="media-preview">
              <video src={object.mediaUrl} autoPlay muted loop playsInline controls aria-label={`${object.mediaName ?? object.name} 영상 미리보기`} />
              <div><strong>{object.mediaName ?? '로컬 영상'}</strong><span>이 브라우저에서만 재생</span></div>
            </div>
          )}
          {object.mediaUrl && playback === 'image' && (
            <div className="media-preview image-preview">
              <img src={object.mediaUrl} alt={`${object.mediaName ?? object.name} 이미지 미리보기`} />
              <div><strong>{object.mediaName ?? '로컬 이미지'}</strong><span>이 브라우저에서만 표시</span></div>
            </div>
          )}
          <label className="media-upload">
            {(playback === 'video' || (!playback && allowsVideo(object.kind))) ? <VideoCamera size={21} /> : <ImageSquare size={21} />}
            <span><strong>{object.mediaUrl ? '파일 교체' : label}</strong><small>{mediaHint(object.kind)}</small></span>
            <input type="file" accept={mediaAccept(object.kind)} onChange={(event) => handleMedia(event.target.files?.[0])} />
          </label>
        </section>
      )}

      <section className="property-section view-content-section">
        <div className="property-title">
          <h3>보기 모드</h3>
          <button
            className={`view-mode-toggle ${object.viewEnabled ? 'active' : ''}`}
            type="button"
            disabled={!object.viewContent}
            onClick={() => onUpdate({ viewEnabled: !object.viewEnabled })}
            aria-pressed={!!object.viewEnabled}
          >
            {object.viewEnabled ? <Check size={13} weight="bold" /> : <BookOpen size={13} />}
            {object.viewEnabled ? '사용 중' : '사용 안 함'}
          </button>
        </div>
        <p className="property-help view-help">H 이동 모드에서 오브젝트를 클릭하면 <strong>Read</strong> 버튼이 표시됩니다.</p>
        {object.viewContent && (
          <div className="view-content-current">
            <span>{object.viewContent.type === 'pdf' ? <FilePdf size={18} /> : object.viewContent.type === 'website' ? <Globe size={18} /> : <ImageSquare size={18} />}</span>
            <div><strong>{object.viewContent.name}</strong><small>이 브라우저의 로컬 저장소</small></div>
            <button type="button" onClick={clearViewContent} aria-label="보기 콘텐츠 제거"><X size={15} /></button>
          </div>
        )}
        <div className="view-content-options">
          <label>
            <FilePdf size={19} weight="duotone" />
            <span><strong>PDF</strong><small>로컬 파일</small></span>
            <input type="file" accept="application/pdf" onChange={(event) => handleViewFile('pdf', event.target.files?.[0])} />
          </label>
          <label>
            <ImageSquare size={19} weight="duotone" />
            <span><strong>이미지</strong><small>JPG, PNG</small></span>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => handleViewFile('image', event.target.files?.[0])} />
          </label>
        </div>
        <div className="website-content-input">
          <Globe size={17} />
          <input value={websiteDraft} onChange={(event) => setWebsiteDraft(event.target.value)} placeholder="https://example.com" aria-label="보기 웹사이트 주소" />
          <button type="button" onClick={saveWebsite} disabled={!websiteDraft.trim()}>등록</button>
        </div>
        <p className="property-help">PDF · 웹사이트 · 이미지 중 하나만 등록되며, 새 항목을 등록하면 기존 항목을 교체합니다.</p>
      </section>
    </aside>
  );
}
